<!-- Render Summary Report Dashboard -->
<div>
  <!-- Dashboard Header -->
  <div class="dashboard-header" fxLayout="column" fxLayoutGap="20px">
    <div fxLayout="row" fxLayoutAlign="space-between center">
      <div fxLayout="column">
        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="12px">
          <h1 class="dashboard-title">Render Summary</h1>
          <mat-icon class="share-button link"
                    (click)="copyShareableUrl()"
                    matTooltip="Copy shareable link to clipboard"
                    matTooltipPosition="below">
            share
          </mat-icon>
        </div>
        <h2 class="company-name">{{ selectedCompany?.name || selectedCompany?.code }} &bull; {{ getDisplayMonthYear() }}</h2>
      </div>
    </div>

    <div fxLayout="row wrap"
         fxLayout.lt-md="column"
         fxLayoutAlign="end center"
         fxLayoutAlign.lt-md="stretch start"
         fxLayoutGap="12px">

      <!-- Month Navigation Controls -->
      <div fxLayout="row"
           fxLayoutAlign="center center"
           fxLayoutGap="4px"
           class="month-nav-container">
        <button mat-icon-button
                (click)="navigateToPreviousMonth()"
                [disabled]="isPreviousMonthDisabled()"
                matTooltip="Previous Month"
                style="flex-shrink: 0;">
          <mat-icon>chevron_left</mat-icon>
        </button>

        <mat-form-field appearance="outline" class="date-selector" style="width: 180px;">
          <mat-label>Select Month</mat-label>
          <input matInput
                 [matDatepicker]="monthPicker"
                 [(ngModel)]="selectedMonthDate"
                 [min]="minDate"
                 [max]="maxDate"
                 readonly>
          <mat-datepicker-toggle matSuffix [for]="monthPicker"></mat-datepicker-toggle>
          <mat-datepicker #monthPicker
                          startView="multi-year"
                          (monthSelected)="setMonthAndYear($event, monthPicker)">
          </mat-datepicker>
        </mat-form-field>

        <button mat-icon-button
                (click)="navigateToNextMonth()"
                [disabled]="isNextMonthDisabled()"
                matTooltip="Next Month"
                style="flex-shrink: 0;">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>

      <!-- Product Filter -->
      <mat-form-field appearance="outline" class="product-filter" style="width: 240px;">
        <mat-label>Product</mat-label>
        <mat-select [value]="selectedProductFilter ?? ALL" (selectionChange)="onProductFilterChange($event)">
          <mat-option [value]="ALL">All Products</mat-option>
          <mat-option *ngFor="let product of productOptions" [value]="product.productName">
            {{ product.product }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Renderer Type Filter -->
      <mat-form-field appearance="outline" class="product-filter" style="width: 180px;">
        <mat-label>Renderer</mat-label>
        <mat-select [value]="selectedRendererTypeFilter ?? ALL" (selectionChange)="onRendererTypeFilterChange($event)">
          <mat-option [value]="ALL">All</mat-option>
          <mat-option *ngFor="let option of rendererTypeOptions" [value]="option.value">{{ option.label }}</mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Export Buttons -->
      <button mat-raised-button
              color="primary"
              (click)="exportToCsv()"
              [disabled]="loading || !rawData || rawData.length === 0 || !dataMatchesSelection"
              style="flex-shrink: 0;">
        <mat-icon>download</mat-icon>
        Export CSV
      </button>

      <button mat-raised-button
              color="primary"
              (click)="saveChartAsPNG()"
              [disabled]="loading || !rawData || rawData.length === 0 || !dataMatchesSelection"
              style="flex-shrink: 0;">
        <mat-icon>image</mat-icon>
        Export PNG
      </button>
    </div>
  </div>

  <!-- Dashboard Content -->
  <div class="dashboard-content" fxLayout="column" fxLayoutGap="16px">

    <!-- Key Metrics Row -->
    <div class="metrics-sidebar">
      <div class="metrics-row">

        <!-- Total Renders -->
        <mat-card class="metric-card">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(210, 90%, 55%)'">{{ totalRenders | number }}</div>
            <div class="metric-label">Total Renders</div>
            <!-- the report counts what it loaded, so say when the month had more than one page -->
            <div *ngIf="totalAvailable > rawData.length; else thisMonth" class="metric-subtitle text-warning">
              Showing the first {{ rawData.length | number }} of {{ totalAvailable | number }}
            </div>
            <ng-template #thisMonth><div class="metric-subtitle">This month</div></ng-template>
          </mat-card-content>
        </mat-card>

        <!-- Success Rate -->
        <mat-card class="metric-card">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(145, 60%, 55%)'">{{ successRate }}%</div>
            <div class="metric-label">Success Rate</div>
            <div class="metric-subtitle">{{ completedRenders | number }} completed</div>
          </mat-card-content>
        </mat-card>

        <!-- Average Render Time -->
        <mat-card class="metric-card">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(60, 80%, 55%)'">{{ formatDuration(averageRenderTimeSeconds) }}</div>
            <div class="metric-label">Avg Render Time</div>
            <div class="metric-subtitle">Per job</div>
          </mat-card-content>
        </mat-card>

        <!-- Failed -->
        <mat-card class="metric-card">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(330, 90%, 55%)'">{{ failedRenders | number }}</div>
            <div class="metric-label">Failed</div>
            <div class="metric-subtitle">Needs attention</div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <!-- Charts Row -->
    <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px">

      <!-- Daily Render Volume Chart -->
      <mat-card class="chart-card" fxFlex="50" fxFlex.lt-md="100">
        <mat-card-header>
          <mat-card-title [style.color]="'hsl(190, 70%, 55%)'">Daily Render Volume</mat-card-title>
          <mat-card-subtitle>Daily breakdown for {{ getDisplayMonthYear() }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div *ngIf="!loading && rawData && rawData.length > 0" class="chart-container">
            <canvas id="dailyCanvas"
                    baseChart
                    [datasets]="dailyChartData"
                    [labels]="dailyChartLabels"
                    [options]="barChartOptions"
                    [legend]="true"
                    [chartType]="'bar'">
            </canvas>
          </div>
          <div *ngIf="loading" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-spinner diameter="60"></mat-spinner>
            <p>Loading chart data...</p>
          </div>
          <div *ngIf="!loading && (!rawData || rawData.length === 0)" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-icon style="font-size: 48px; width: 48px; height: 48px; color: var(--text-secondary);">bar_chart</mat-icon>
            <p>No render data available for the selected period.</p>
            <p style="font-size: 14px;">Try selecting a different month or check your filters.</p>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Product Breakdown Chart -->
      <mat-card class="chart-card" fxFlex="50" fxFlex.lt-md="100">
        <mat-card-header>
          <mat-card-title [style.color]="'hsl(280, 70%, 55%)'">By Product Type</mat-card-title>
          <mat-card-subtitle>Render distribution by product</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div *ngIf="!loading && rawData && rawData.length > 0 && productChartData.length > 0" class="chart-container">
            <canvas id="productCanvas"
                    baseChart
                    [data]="productChartData"
                    [labels]="productChartLabels"
                    [options]="pieChartOptions"
                    [colors]="productChartColors"
                    [chartType]="'doughnut'">
            </canvas>
          </div>
          <div *ngIf="loading" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-spinner diameter="60"></mat-spinner>
            <p>Loading chart data...</p>
          </div>
          <div *ngIf="!loading && (!rawData || rawData.length === 0 || productChartData.length === 0)" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-icon style="font-size: 48px; width: 48px; height: 48px; color: var(--text-secondary);">pie_chart</mat-icon>
            <p>No product data available for the selected period.</p>
            <p style="font-size: 14px;">Try selecting a different month or check your filters.</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</div>
